import { Fragment, ReactElement, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from 'support/helpers/cn/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

import { SidebarBadge } from './components/SidebarBadge/SidebarBadge';

type SidebarItemProps = {
  href?: string;
  label: ReactNode;
  expanded: boolean;
  sidebarBreak?: boolean;
  externalLink?: boolean;
  icon?: ReactElement;
  badgeContent?: string | number;
  badgeVariant?: 'notification' | 'new';
  onClick?: () => void;
};
export function NavigationSidebarItem({
  href,
  label,
  icon,
  externalLink,
  onClick,
  expanded,
  badgeContent,
  badgeVariant = 'notification',
}: SidebarItemProps) {
  const content = (
    <div className="flex w-full flex-row items-center justify-between">
      <div className="flex w-full flex-row items-center">
        <div className="size-6 shrink-0">{icon}</div>
        {expanded ? (
          <span className="flex w-full items-center justify-between pl-3 text-sm leading-none">{label}</span>
        ) : null}
      </div>

      {badgeContent ? (
        <SidebarBadge size={expanded ? 'normal' : 'small'} content={badgeContent} variant={badgeVariant} />
      ) : null}
    </div>
  );

  const Wrapper = expanded ? Fragment : createToolTip(label);

  if (!href) {
    return (
      <Wrapper>
        <button className={getButtonClasses()} onClick={onClick}>
          {content}
        </button>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {externalLink ? (
        <a
          href={href}
          target="_blank"
          className={getLinkClasses({ isActive: false })}
          rel="noreferrer"
          onClick={onClick}
        >
          {content}
        </a>
      ) : (
        <NavLink to={href} className={getLinkClasses} onClick={onClick}>
          {content}
        </NavLink>
      )}
    </Wrapper>
  );
}

const getLinkClasses = ({ isActive }: { isActive: boolean }) =>
  cn(
    isActive ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-700',
    'group relative -mx-2 rounded-md p-2 text-sm font-medium',
  );

const getButtonClasses = () => cn(getLinkClasses({ isActive: false }), 'min-w-full');

const createToolTip =
  (tooltip: ReactNode) =>
  // eslint-disable-next-line react/display-name
  ({
    children,
  }: {
    children: React.ReactElement<any, string | React.JSXElementConstructor<any>> & {
      ref?: React.RefObject<HTMLElement>;
    };
  }) => {
    return (
      <Tooltip placement="right" spacing={30}>
        <TooltipTrigger>{children}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  };
